import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { brandBuybackArticles } from "../content/brandBuyback";

// Canvas preview-safe fallback.
// Enclopedia本番環境では以下に置き換えてください。
// import { headRewrite } from "../function/headRewrite";
const headRewrite = () => {};

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.18,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.8vw, 34px)",
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 700,
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  noticeBody: {
    fontSize: 13,
    lineHeight: 1.9,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 540,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 390,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 22,
    width: 270,
    height: 270,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 24,
    top: 44,
    width: 190,
    height: 190,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroMock: {
    position: "relative",
    width: "76%",
    maxWidth: 430,
    aspectRatio: "1 / 1",
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    overflow: "hidden",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "84%",
      maxWidth: 320,
    },
  },
  heroMockImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroMockLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  heroMockTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(24px, 3vw, 36px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  thesisCard: {
    border: "1px solid #111",
    minHeight: 315,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.72,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  valuationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  valuationCard: {
    border: "1px solid #111",
    minHeight: 315,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 192,
  },
  brandTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.4),
  },
  brandTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  listBlock: {
    borderTop: "1px solid #111",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "72px 280px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  listIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  listTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.72,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  flowGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  flowCard: {
    border: "1px solid #111",
    minHeight: 255,
    padding: theme.spacing(2.8),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  flowStep: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  flowTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 22,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  assuranceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  assuranceItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 180,
  },
  assuranceTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1.5),
  },
  noteBox: {
    marginTop: theme.spacing(4),
    border: "1px solid #111",
    padding: theme.spacing(3),
    background: "rgba(255,255,255,0.18)",
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(3),
  },
  faqQuestion: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  finalButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  indexHero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
  },
  indexIntro: {
    maxWidth: 820,
  },
  articleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  articleCard: {
    border: "1px solid #111",
    minHeight: 330,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#111",
    textDecoration: "none",
    transition: "background 0.2s ease, color 0.2s ease",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  articleCategory: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  articleTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  articleCardBody: {
    fontSize: 14,
    lineHeight: 2,
  },
  articleArrow: {
    marginTop: theme.spacing(3),
    fontWeight: 700,
    fontSize: 14,
  },
  notFound: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(8, 0),
  },
}));


function renderLines(lines) {
  return lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

function Seo({ article, canonicalUrl }) {
  const { title, description, keyword } = article.seo;
  const ogImage = article.hero.image
    ? `${process.env.REACT_APP_IMAGE_URL || ""}${article.hero.image}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "ブランド買取",
            "宅配買取",
            article.listTitle,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Brand Buyback",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/brand_buyback`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.listTitle,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.faq.items.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        })}
      </script>
    </Helmet>
  );
}

function IndexSeo() {
    const title =
    "ブランド買取｜アーカイブ価値まで見て査定するEnclopedia";

  const description =
    "Enclopediaのブランド買取ページ一覧。デザイナーズブランドのシーズン背景・コレクション性・希少性まで見て査定し、LINE事前査定から宅配買取までご案内します。";

  const keyword =
    "ブランド別買取,デザイナーズブランド 買取,アーカイブ買取,ブランド古着 買取,宅配買取,LINE査定,Enclopedia";

  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/brand_buyback`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Enclopedia",
            url: typeof window !== "undefined" ? window.location.origin : "",
          },
        })}
      </script>
    </Helmet>
  );
}

function BrandBuybackIndex({ classes }) {
  return (
    <div className={classes.root}>
      <IndexSeo />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.indexHero}>
            <div className={classes.indexIntro}>
              <div className={classes.labelRow}>
                <span>BRAND BUYBACK</span>
                <span className={classes.labelLine} />
              </div>
              <Typography variant="h1" component="h1" className={classes.heroTitle}>
                ブランドごとの価値を、
                <br />
                文脈まで見て査定する。
              </Typography>
              <Typography component="p" className={classes.heroBody}>
                Enclopediaでは、シーズン背景・コレクション性・希少性を重視したブランド別買取ページを用意しています。
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.articleGrid}>
              {brandBuybackArticles.map((article) => (
                <a
                  href={`/brand_buyback/${article.slug}`}
                  className={classes.articleCard}
                  key={article.slug}
                >
                  <div>
                    <div className={classes.articleCategory}>{article.category}</div>
                    <Typography component="h2" className={classes.articleTitle}>
                      {article.listTitle}
                    </Typography>
                    <Typography component="p" className={classes.articleCardBody}>
                      {article.listDescription}
                    </Typography>
                  </div>
                  <div className={classes.articleArrow}>ページを見る →</div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

function BrandBuybackNotFound({ classes }) {
  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.notFound}>
          <div>
            <Typography component="h1" className={classes.heading}>
              買取ページが見つかりませんでした。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/brand_buyback">
              ブランド別買取一覧へ戻る
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function BrandBuybackDetail({ classes, article }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${article.seo.canonicalPath}`;
  const { hero, thesis } = article;

  return (
    <div className={classes.root}>
      <Seo article={article} canonicalUrl={canonicalUrl} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{hero.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {renderLines(hero.titleLines)}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {hero.body}
                </Typography>
                <div className={classes.buttonRow}>
                  <Button className={classes.primaryButton} component="a" href={hero.primaryCtaHref}>
                    {hero.primaryCtaLabel}
                  </Button>
                  <Button className={classes.secondaryButton} component="a" href={hero.secondaryCtaHref}>
                    {hero.secondaryCtaLabel}
                  </Button>
                </div>
                <div className={classes.keywordLine}>
                  {hero.keywords.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < hero.keywords.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{hero.number}</div>
                  <div className={classes.heroMock}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL || ""}${hero.image}`}
                      alt={hero.imageAlt}
                      className={classes.heroMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>{article.intro.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.intro.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.intro.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {thesis.text}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    {thesis.keywords.map((keyword, index) => (
                      <React.Fragment key={keyword}>
                        <span>{keyword}</span>
                        {index < thesis.keywords.length - 1 && <span>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.valuation.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.valuation.titleLines)}
            </Typography>
            <div className={classes.valuationGrid}>
              {article.valuation.items.map((item) => (
                <div className={classes.valuationCard} key={item.number}>
                  <div>
                    <div className={classes.cardNumber}>{item.number}</div>
                    <div className={classes.cardTitle}>{item.title}</div>
                    <div className={classes.smallRule} />
                    <Typography component="p" className={classes.body}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.targetLines.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.targetLines.titleLines)}
            </Typography>
            <div className={classes.brandGrid}>
              {article.targetLines.items.map((item) => (
                <div className={classes.brandItem} key={item.title}>
                  <div className={classes.brandTag}>{item.tag}</div>
                  <Typography component="h3" className={classes.brandTitle}>
                    {item.title}
                  </Typography>
                  <Typography component="p" className={classes.noteBody}>
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.highValueItems.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.highValueItems.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.highValueItems.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {article.highValueItems.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {renderLines(article.quote.textLines)}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.assessmentDifference.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.assessmentDifference.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.assessmentDifference.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {article.assessmentDifference.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.flow.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.flow.titleLines)}
            </Typography>
            <div className={classes.flowGrid}>
              {article.flow.items.map((item) => (
                <div className={classes.flowCard} key={item.step}>
                  <div>
                    <div className={classes.flowStep}>{item.step}</div>
                    <Typography component="h3" className={classes.flowTitle}>
                      {item.title}
                    </Typography>
                    <Typography component="p" className={classes.noteBody}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.assurance.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.assurance.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.assurance.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.assuranceGrid}>
                  {article.assurance.items.map((item) => (
                    <div className={classes.assuranceItem} key={item.title}>
                      <div className={classes.assuranceTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={classes.noteBox}>
                  <Typography component="p" className={classes.noticeBody}>
                    {article.assurance.notice}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section} id="faq">
            <div className={classes.labelRow}>
              <span>{article.faq.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.faq.titleLines)}
            </Typography>
            <div className={classes.faqGrid}>
              {article.faq.items.map((faq) => (
                <div className={classes.faqItem} key={faq.question}>
                  <div className={classes.faqQuestion}>{faq.question}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {faq.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              {renderLines(article.finalCta.titleLines)}
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              {article.finalCta.body}
            </Typography>
            <div className={classes.finalButtons}>
              <Button className={classes.primaryButton} component="a" href={article.finalCta.primaryHref}>
                {article.finalCta.primaryLabel}
              </Button>
              <Button className={classes.secondaryButton} component="a" href={article.finalCta.secondaryHref}>
                {article.finalCta.secondaryLabel}
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function BrandBuybackScreen() {
  const classes = useStyles();
  const { slug } = useParams();

  if (!slug) {
    return <BrandBuybackIndex classes={classes} />;
  }

  const article = brandBuybackArticles.find((item) => item.slug === slug);

  if (!article) {
    return <BrandBuybackNotFound classes={classes} />;
  }

  return <BrandBuybackDetail classes={classes} article={article} />;
}
